/**
 * Route paths for mops-web. Admin tool paths are added incrementally as each
 * tool migrates from `@orn/web` — see the batched migration strategy under
 * ORN-526.
 */
export const PATHS = {
  HOME: '/',
  /** Accounts tool — search every household and read the selected one's header. */
  ACCOUNTS: '/accounts',
  ALPHA_ALLOW_LIST: '/alpha-allow-list',
  /** Assistant tool — parent route wrapping the memory + threads sub-tabs. */
  ASSISTANT: '/assistant',
  /** Memory sub-tab of the Assistant tool — lists memories and supports deletion. */
  ASSISTANT_MEMORY: '/assistant/memory',
  /** Threads sub-tab — left-list/right-detail inspector with hard-delete of one or all threads for a user. */
  ASSISTANT_THREADS: '/assistant/threads',
  /** Feedback sub-tab — global newest-first table of `/issue` submissions, each row deep-linking into Threads. */
  ASSISTANT_FEEDBACK: '/assistant/feedback',
  /** Benchmarks sub-tab — run and view on-demand two-commit assistant benchmarks (staging-only). */
  ASSISTANT_BENCHMARKS: '/assistant/benchmarks',
  BANK_CONNECTIONS: '/bank-connections',
  BILL_MANAGEMENT: '/bill-management',
  /** Channel health tool — whether texts and email reach every active member, and the channels needing a look. */
  CHANNEL_HEALTH: '/channel-health',
  DELETE_ACCOUNT: '/delete-account',
  FEATURE_GATES: '/feature-gates',
  /** Detail route for editing an existing gate by id. */
  FEATURE_GATE_EDIT: '/feature-gates/:id',
  /** New-gate creation form — flat path, kept in sync with the list view. */
  FEATURE_GATE_NEW: '/feature-gates/new',
  IMPERSONATE_USER: '/impersonate-user',
  OBJECT_INSPECTOR: '/object-inspector',
  PROCESSOR_STATUSES: '/processor-statuses',
  RELEASES: '/releases',
  /** Static assets tool — upload, link, and delete files on the marketing CDN. */
  STATIC_ASSETS: '/static-assets',
  /** Work items tool — parent route wrapping the items + schedules sub-tabs. */
  WORK_ITEMS: '/work-items',
  /** Items sub-tab — fleet-wide read-only table of the assistant's work items. */
  WORK_ITEMS_LIST: '/work-items/items',
  /** Schedules sub-tab — fleet-wide table of the standing instructions that emit work items. */
  WORK_ITEM_SCHEDULES: '/work-items/schedules',
} as const;

export type PathKey = keyof typeof PATHS;
export type PathValue = (typeof PATHS)[PathKey];

/** Builds the URL for editing a feature gate by id. */
export function featureGateEditPath(id: string): string {
  return `/feature-gates/${id}`;
}

/**
 * Builds the New-gate form URL, optionally pre-filling the gate name. Used to
 * configure a gate that exists in code (the `FeatureGate` registry) but has no
 * `feature_gates` row yet, so the operator lands on the form with the correct
 * name already entered. Gate names are public and `[a-z0-9-]+`, so they're safe
 * to carry in the query string.
 */
export function featureGateNewPath(presetName?: string): string {
  if (!presetName) {
    return PATHS.FEATURE_GATE_NEW;
  }
  return `${PATHS.FEATURE_GATE_NEW}?name=${encodeURIComponent(presetName)}`;
}

/** Builds a link to the Feedback tab anchored to (scroll + highlight) one row. */
export function assistantFeedbackAnchorPath(feedbackId: string): string {
  return `${PATHS.ASSISTANT_FEEDBACK}?feedbackId=${encodeURIComponent(feedbackId)}`;
}

/** Builds a link to the Benchmarks tab focused on one run (opens its detail pane). */
export function assistantBenchmarkRunPath(runId: string): string {
  return `${PATHS.ASSISTANT_BENCHMARKS}?runId=${encodeURIComponent(runId)}`;
}

/**
 * Builds a deep link into the Threads inspector. `threadId` alone is enough —
 * the inspector loads a transcript from the thread id and backfills user/family
 * from the result — so `userId` is optional (provide it when known to also
 * populate the left thread list). The inspector never reads `familyId`: it takes
 * the family from the user's own record. Pass `turnId` to additionally scroll
 * to (and highlight) a specific turn.
 *
 * Only defined keys are emitted: `URLSearchParams` would otherwise stringify an
 * absent `userId` to the literal `"undefined"`, which the inspector reads back
 * as a truthy garbage id.
 */
export function assistantThreadDeepLinkPath(params: {
  userId?: string;
  familyId?: string;
  threadId: string;
  turnId?: string;
}): string {
  const present = Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined);
  const query = new URLSearchParams(present).toString();
  return `${PATHS.ASSISTANT_THREADS}?${query}`;
}

/** Parsed identifiers from the Threads "jump to thread" lookup box. */
export interface IThreadJump {
  threadId: string;
  turnId?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parse the lookup box's raw input into a thread jump. Accepts a bare thread id
 * or a thread id followed (whitespace- or comma-separated) by a turn id — the
 * shape an operator gets by copying `threadId` / `turnId` out of a Sentry
 * report. Returns null when the first token isn't a UUID, so the caller can
 * reject the paste instead of navigating to a junk thread. A non-UUID second
 * token is ignored (thread-only jump) rather than failing the whole parse.
 */
export function parseThreadJump(raw: string): IThreadJump | null {
  const tokens = raw
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (tokens.length === 0 || !UUID_RE.test(tokens[0])) {
    return null;
  }
  const jump: IThreadJump = { threadId: tokens[0] };
  if (tokens[1] !== undefined && UUID_RE.test(tokens[1])) {
    jump.turnId = tokens[1];
  }
  return jump;
}

/**
 * Next query string for the Threads inspector when the selected thread changes,
 * so the URL always reflects the open transcript. Preserves any other params
 * (e.g. `userId` / `familyId`).
 *
 * `turnId` is replaced, never carried over: a plain thread switch has no
 * specific turn to scroll to, so it clears, while opening a search hit passes
 * the turn the match landed on. A stale highlight from the previous thread
 * would point at a turn the new one doesn't have.
 *
 * Passing `threadId = null` (e.g. after a delete or a user switch) drops the
 * thread from the URL entirely.
 */
export function nextThreadSearchParams(
  current: URLSearchParams,
  threadId: string | null,
  turnId: string | null = null
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete('turnId');
  if (threadId === null) {
    next.delete('threadId');
    return next;
  }
  next.set('threadId', threadId);
  if (turnId !== null) {
    next.set('turnId', turnId);
  }
  return next;
}

/**
 * Builds a link to the Items tab narrowed to the work one schedule has emitted.
 * The tab drops its hide-archived default when it lands carrying this filter:
 * what a schedule's emissions answer is how often it has fired, and most of
 * what it emitted has since been put away.
 */
export function workItemsForSchedulePath(scheduleId: string): string {
  return `${PATHS.WORK_ITEMS_LIST}?scheduleId=${encodeURIComponent(scheduleId)}`;
}

/**
 * The tools that pre-select a user from a {@link userToolPath} link. Delete
 * account is deliberately absent: an irreversible deletion starts from its own
 * picker, never from a link that chose the user for it.
 */
export type UserToolPath =
  | typeof PATHS.ASSISTANT_MEMORY
  | typeof PATHS.ASSISTANT_THREADS
  | typeof PATHS.BANK_CONNECTIONS
  | typeof PATHS.BILL_MANAGEMENT
  | typeof PATHS.IMPERSONATE_USER
  | typeof PATHS.WORK_ITEMS_LIST;

/** Builds a link into a user-scoped tool with that user already selected in its picker. */
export function userToolPath(path: UserToolPath, userId: string): string {
  return `${path}?${new URLSearchParams({ userId }).toString()}`;
}

/**
 * Next query string for a user-scoped tool when its picker changes, so the URL
 * keeps naming the user on screen and the browser's Back button lands back on
 * them. Preserves every other param; `userId = null` drops the key.
 */
export function nextUserSearchParams(current: URLSearchParams, userId: string | null): URLSearchParams {
  const next = new URLSearchParams(current);
  if (userId === null) {
    next.delete('userId');
    return next;
  }
  next.set('userId', userId);
  return next;
}

/**
 * The user id a {@link userToolPath} link carries, or null unless it is
 * UUID-shaped, so a mangled link opens the tool with an empty picker instead
 * of querying a garbage id. A link carries no family: the tool reads it from
 * the user's own record, so a URL cannot pair a user with another household.
 */
export function readLinkedUserId(params: URLSearchParams): string | null {
  const userId = params.get('userId');
  return userId !== null && UUID_RE.test(userId) ? userId : null;
}

/** Builds a link that opens the Object inspector on one id, internal or external. */
export function objectInspectorPath(id: string): string {
  return `${PATHS.OBJECT_INSPECTOR}?id=${encodeURIComponent(id)}`;
}

/** Builds a link that opens the Accounts tool on one household. */
export function accountsFamilyPath(familyId: string): string {
  return `${PATHS.ACCOUNTS}?familyId=${encodeURIComponent(familyId)}`;
}
